/**
 * Fuzzy column-name matcher used during config import.
 *
 * Purely functional; no React, no stores. Import review UI calls this
 * to propose a candidate mapping for every column from the exported
 * config that does NOT exist verbatim in the current dataset.
 *
 * Strategy (deliberately boring):
 *  1. Normalize both sides (casefold, TR diacritic fold, collapse
 *     separators).
 *  2. Score candidates with `similarity(a, b)` ∈ [0, 1]. The score
 *     blends Levenshtein distance with a sqlType-agreement bonus so a
 *     NUMERIC→NUMERIC match beats NUMERIC→VARCHAR at the same edit
 *     distance.
 *  3. Return candidates above `MIN_SCORE`, sorted best-first.
 *
 * No auto-apply here — the UI always shows all candidates to the
 * user per D-022. This module just ranks them.
 */
import type { ColumnMeta } from '@/types/dataset';

/** Floor for inclusion in the candidate list. Tuned empirically. */
export const MIN_SCORE = 0.35;

/** Weight of sqlType agreement in the blended score. */
const TYPE_BONUS = 0.15;

/**
 * Casefold + fold Turkish diacritics + collapse `_`/`-`/whitespace to
 * single spaces. `"Müşteri_Adı"` and `"musteri adi"` become identical.
 */
export function normalize(s: string): string {
  return s
    .toLocaleLowerCase('tr-TR')
    .replace(/ı/g, 'i')
    .replace(/ş/g, 's')
    .replace(/ğ/g, 'g')
    .replace(/ü/g, 'u')
    .replace(/ö/g, 'o')
    .replace(/ç/g, 'c')
    .replace(/[_\-\s]+/g, ' ')
    .trim();
}

/** Classic Levenshtein. O(n·m) time, O(min(n, m)) space. */
export function levenshtein(a: string, b: string): number {
  if (a === b) return 0;
  if (a.length === 0) return b.length;
  if (b.length === 0) return a.length;
  // Swap so `b` is the shorter one — keeps the row buffer small.
  if (a.length < b.length) [a, b] = [b, a];

  let prev = new Array<number>(b.length + 1);
  let curr = new Array<number>(b.length + 1);
  for (let j = 0; j <= b.length; j++) prev[j] = j;

  for (let i = 1; i <= a.length; i++) {
    curr[0] = i;
    for (let j = 1; j <= b.length; j++) {
      const cost = a.charCodeAt(i - 1) === b.charCodeAt(j - 1) ? 0 : 1;
      curr[j] = Math.min(
        curr[j - 1]! + 1, // insertion
        prev[j]! + 1, // deletion
        prev[j - 1]! + cost, // substitution
      );
    }
    [prev, curr] = [curr, prev];
  }
  return prev[b.length]!;
}

/**
 * Blended similarity score in [0, 1]. Higher is better.
 * Exact (normalized) match returns 1. Equal sqlType adds TYPE_BONUS,
 * capped at 1.
 */
export function similarity(
  source: ColumnMeta,
  target: ColumnMeta,
): number {
  const a = normalize(source.name);
  const b = normalize(target.name);
  if (a.length === 0 && b.length === 0) return 0;
  const dist = levenshtein(a, b);
  const base = 1 - dist / Math.max(a.length, b.length);
  const bonus = source.sqlType === target.sqlType ? TYPE_BONUS : 0;
  return Math.min(1, base + bonus);
}

export type MatchCandidate = {
  column: ColumnMeta;
  score: number;
};

/**
 * Rank `candidates` against `source`, best-first. Drops everything
 * below `MIN_SCORE` unless the caller lowers it. Stable ordering:
 * same score → preserve input order.
 */
export function rankCandidates(
  source: ColumnMeta,
  candidates: readonly ColumnMeta[],
  minScore: number = MIN_SCORE,
): MatchCandidate[] {
  const scored: MatchCandidate[] = [];
  for (const c of candidates) {
    const score = similarity(source, c);
    if (score >= minScore) scored.push({ column: c, score });
  }
  // Descending by score; stable sort preserves candidate input order on ties.
  scored.sort((x, y) => y.score - x.score);
  return scored;
}

/**
 * Convenience: given an exported column and the current dataset's
 * columns, returns `null` when no verbatim OR fuzzy match clears
 * the floor. Exact-name matches are preferred even if sqlType shifted.
 */
export function bestMatch(
  source: ColumnMeta,
  candidates: readonly ColumnMeta[],
): ColumnMeta | null {
  const exact = candidates.find((c) => c.name === source.name);
  if (exact) return exact;
  const ranked = rankCandidates(source, candidates);
  return ranked[0]?.column ?? null;
}
